/**
 * near-me-schema — Plan feature display data
 *
 * Maps each surfaced feature to per-plan display text the pricing UI renders:
 *   - `true`   → enabled, render with a checkmark
 *   - `false`  → locked, render with a lock icon
 *   - `string` → display the string (e.g. "10", "Unlimited")
 *   - `{ text, helpText? }` → display text with an optional tooltip
 *
 * `getNearMePlanFeatureDisplay(planId)` walks this list to build the
 * included/locked feature lists shown on a plan card.
 *
 * @module near-me-schema/plan-features-data
 */

import type { NearMePlanId } from './plan-limits.js';

/** One row's display value with an optional tooltip. */
export interface FeatureDisplayValue {
  text: string;
  helpText?: string;
}
export type FeaturePlanCell = boolean | string | FeatureDisplayValue;

export interface FeatureDisplayItem {
  /** Stable feature key. Matches NearMePlanLimits where it's a limit too. */
  key: string;
  /** Human-readable feature label (rendered next to the icon). */
  label: string;
  /** Optional explanation tooltip (rendered alongside the label as a help icon). */
  helpText?: string;
  /** Per-plan display value. */
  byPlan: Record<NearMePlanId, FeaturePlanCell>;
  /** Optional sort key — lower renders first. Default 100. */
  order?: number;
}

/**
 * One row per feature the pricing UI surfaces. Deliberately lean and honest —
 * only levers we actually enforce, plus the always-on value (listings,
 * reviews, dashboard) so the Free tier reads as genuinely useful. Seeker-side
 * actions (browse, search, save, enquire) are free for everyone, so they are
 * not plan differentiators and do not appear here.
 */
export const NEAR_ME_PLAN_FEATURE_DISPLAY_DATA: FeatureDisplayItem[] = [
  {
    key: 'listings',
    label: 'Business listings',
    order: 10,
    byPlan: {
      free: 'Unlimited listings',
      pro: 'Unlimited listings',
      premium: 'Unlimited listings',
    },
  },
  {
    key: 'monthlyLeadCap',
    label: 'Leads per month',
    helpText: 'Enquiries you can view and act on each month across all your listings.',
    order: 20,
    byPlan: { free: '10 leads a month', pro: 'Unlimited leads', premium: 'Unlimited leads' },
  },
  {
    key: 'reviews',
    label: 'Reviews & ratings',
    order: 30,
    byPlan: { free: true, pro: true, premium: true },
  },
  {
    key: 'analytics',
    label: 'Owner dashboard',
    order: 40,
    byPlan: { free: true, pro: true, premium: true },
  },
  {
    key: 'featuredPlacement',
    label: 'Featured placement',
    helpText: 'Pinned above other businesses in your category and area, with a Featured badge.',
    order: 50,
    byPlan: { free: false, pro: false, premium: true },
  },
];

export interface FeatureChipDisplay {
  key: string;
  label: string;
  helpText?: string;
  /** Display value. `null` when the feature is a plain boolean (label is the text). */
  value: string | null;
  /** `true` when the feature is enabled (boolean true or non-empty string). */
  included: boolean;
}

/**
 * Build the feature chips for a plan, split into included (checkmarks) and
 * locked (lock icon). Items are sorted by `order` ascending. For boolean-true
 * features the `label` is the chip text (value `null`); for string-valued
 * features the value is the chip text; locked (boolean-false) features return
 * `value: null` in the `locked` bucket.
 */
export function getNearMePlanFeatureDisplay(planId: NearMePlanId): {
  included: FeatureChipDisplay[];
  locked: FeatureChipDisplay[];
} {
  const included: FeatureChipDisplay[] = [];
  const locked: FeatureChipDisplay[] = [];
  const rows = [...NEAR_ME_PLAN_FEATURE_DISPLAY_DATA].sort(
    (a, b) => (a.order ?? 100) - (b.order ?? 100),
  );

  for (const item of rows) {
    const cell = item.byPlan[planId];
    let value: string | null = null;
    let isIncluded = false;

    if (typeof cell === 'boolean') {
      isIncluded = cell;
    } else if (typeof cell === 'string') {
      isIncluded = cell.length > 0;
      value = cell;
    } else {
      isIncluded = cell.text.length > 0;
      value = cell.text;
    }

    const chip: FeatureChipDisplay = {
      key: item.key,
      label: item.label,
      helpText: item.helpText,
      value,
      included: isIncluded,
    };
    (isIncluded ? included : locked).push(chip);
  }

  return { included, locked };
}
